export type AuthScope = "user" | "providers" | "admins";
export type OAuthProvider = "google" | "microsoft" | "facebook";

export const API_ROUTES = {
  auth: {
    base: "/api/auth",
    signup: "/api/auth/signup",
    refresh: "/api/auth/refresh",
    logout: "/api/auth/logout",
    resendVerificationEmail: "/api/auth/resend-verification-email",
    forgotPassword: "/api/auth/forgot-password",
    resetPassword: "/api/auth/reset-password",
    oauth: "/api/auth/oauth",
    login: "/api/auth/login",
    me: "/api/auth/me",
    verifyEmail: (token?: string) =>
      token
        ? `/api/auth/verify-email?token=${encodeURIComponent(token)}`
        : "/api/auth/verify-email",
    magicLinkRequest: "/api/auth/magic-link/request",
    mfaVerify: "/api/auth/mfa/verify",
    mfaResend: "/api/auth/mfa/resend",
    magicLinkVerify: (token?: string) =>
      token
        ? `/api/auth/magic-link/verify?token=${encodeURIComponent(token)}`
        : "/api/auth/magic-link/verify",
    oauthStart: (provider: OAuthProvider) => `/api/auth/${provider}`,
  },
  admins: {
    auth: {
      base: "/api/admins/auth",
      login: "/api/admins/auth/login",
      refresh: "/api/admins/auth/refresh",
      me: "/api/admins/auth/me",
      logout: "/api/admins/auth/logout",
      mfaVerify: "/api/admins/auth/mfa/verify",
      mfaResend: "/api/admins/auth/mfa/resend",
    },
    providers: {
      search: "/api/admins/providers/search",
      accounts: "/api/admins/providers/accounts",
      onboarding: "/api/admins/providers/onboarding",
      onboardingOptions: "/api/admins/providers/onboarding/options",
      onboardingSnapshot: (providerId: string) =>
        `/api/admins/providers/${providerId}/onboarding`,
      subscription: (providerId: string) =>
        `/api/admins/providers/${providerId}/subscription`,
      subscriptionPlans: (providerId: string) =>
        `/api/admins/providers/${providerId}/subscription-plans/provider`,
      assignSubscription: (providerId: string) =>
        `/api/admins/providers/${providerId}/subscription/assign`,
      publishApproval: (providerId: string) =>
        `/api/admins/providers/${providerId}/publish-approval`,
    },
    insuranceVerifications: {
      list: "/api/admins/insurance-verifications",
      detail: (id: string) => `/api/admins/insurance-verifications/${id}`,
      status: (id: string) => `/api/admins/insurance-verifications/${id}/status`,
      frontCardDownload: (id: string) => `/api/admins/insurance-verifications/${id}/front-card/download`,
      backCardDownload: (id: string) => `/api/admins/insurance-verifications/${id}/back-card/download`,
    },
    insurancePlans: {
      list: "/api/admins/insurance-plans",
      item: (planId: string) => `/api/admins/insurance-plans/${planId}`,
      status: (planId: string) => `/api/admins/insurance-plans/${planId}/status`,
    },
    providerGrowth: {
      demoRequests: "/api/admins/provider-demo-requests",
      demoRequestStatus: (id: string) => `/api/admins/provider-demo-requests/${id}/status`,
      signups: "/api/admins/provider-signups",
    },
    billing: {
      overview: "/api/admins/billing/overview",
      providersSummary: "/api/admins/billing/providers/summary",
      providerDetails: (providerId: string) => `/api/admins/billing/providers/${providerId}/details`,
      providerCharges: (providerId: string) => `/api/admins/billing/providers/${providerId}/charges`,
      patientsSummary: "/api/admins/billing/patients/summary",
      patientDetails: (patientId: string) => `/api/admins/billing/patients/${patientId}/details`,
      patientCharges: (patientId: string) => `/api/admins/billing/patients/${patientId}/charges`,
      chargeRules: "/api/admins/billing/charge-rules",
      chargeRuleById: (id: string) => `/api/admins/billing/charge-rules/${id}`,
      chargeRuleVersions: (id: string) => `/api/admins/billing/charge-rules/${id}/versions`,
    },
    subscription: {
      plans: "/api/admins/subscription/plans",
      plan: (planId: string) => `/api/admins/subscription/plans/${planId}`,
      planFeatures: (planId: string) => `/api/admins/subscription/plans/${planId}/features`,
      features: "/api/admins/subscription/features",
    },
    leads: {
      report: "/api/admins/leads/report",
      providerReport: (providerId: string) => `/api/admins/leads/providers/${providerId}/report`,
    },
    analytics: {
      searchIntelligence: "/api/admins/analytics/search-intelligence",
      emailTracking: "/api/admins/analytics/email-tracking",
    },
    support: {
      tickets: "/api/admins/support/tickets",
      ticket: (ticketId: string) => `/api/admins/support/tickets/${ticketId}`,
      messages: (ticketId: string) => `/api/admins/support/tickets/${ticketId}/messages`,
      status: (ticketId: string) => `/api/admins/support/tickets/${ticketId}/status`,
    },
    settings: {
      serviceIntegrations: "/api/admins/service-integrations",
      serviceIntegration: (serviceKey: string) =>
        `/api/admins/service-integrations/${serviceKey}`,
      emailTemplates: "/api/admins/email-templates",
      emailTemplate: (id: string) => `/api/admins/email-templates/${id}`,
      emailTemplateStatus: (id: string) => `/api/admins/email-templates/${id}/status`,
      mfaSettings: "/api/admins/auth/mfa-settings",
      mfaSettingsByRole: (role: "patient" | "provider" | "admin") =>
        `/api/admins/auth/mfa-settings/${role}`,
      leadScoring: "/api/admins/lead-scoring",
      leadScoringPreview: "/api/admins/lead-scoring/preview",
      cache: "/api/admins/cache",
      cacheItem: "/api/admins/cache/item",
      cacheClearAll: "/api/admins/cache/clear-all",
      communicationCheck: "/api/admins/communication-check/send-test",
    },
    providerCommunications: {
      send: "/api/admins/provider-communications/send",
      list: "/api/admins/provider-communications",
      detail: (id: string) => `/api/admins/provider-communications/${id}`,
    },
    timerJobs: {
      list: "/api/admins/timer-jobs",
      detail: (jobKey: string) => `/api/admins/timer-jobs/${jobKey}`,
      logs: (jobKey: string) => `/api/admins/timer-jobs/${jobKey}/logs`,
      runNow: (jobKey: string) => `/api/admins/timer-jobs/${jobKey}/run-now`,
      enable: (jobKey: string) => `/api/admins/timer-jobs/${jobKey}/enable`,
      disable: (jobKey: string) => `/api/admins/timer-jobs/${jobKey}/disable`,
      start: (jobKey: string) => `/api/admins/timer-jobs/${jobKey}/start`,
      stop: (jobKey: string) => `/api/admins/timer-jobs/${jobKey}/stop`,
    },
    accounts: {
      providers: "/api/admins/accounts/providers",
      providerStatus: (accountId: string) =>
        `/api/admins/accounts/providers/${accountId}/status`,
    },
  },
  providerPortal: {
    scope: "/api/provider-portal/scope",
    dashboard: "/api/provider-portal/dashboard",
    analytics: {
      searchIntelligence: "/api/provider-portal/analytics/search-intelligence",
    },
    auth: {
      base: "/api/provider-portal/auth",
      login: "/api/provider-portal/auth/login",
      refresh: "/api/provider-portal/auth/refresh",
      me: "/api/provider-portal/auth/me",
      logout: "/api/provider-portal/auth/logout",
      mfaVerify: "/api/provider-portal/auth/mfa/verify",
      mfaResend: "/api/provider-portal/auth/mfa/resend",
    },
    appointments: {
      calendar: "/api/provider-portal/appointments/calendar",
      cancel: (appointmentId: string) =>
        `/api/provider-portal/appointments/${appointmentId}/cancel`,
      approve: (appointmentId: string) =>
        `/api/provider-portal/appointments/${appointmentId}/approve`,
      deny: (appointmentId: string) =>
        `/api/provider-portal/appointments/${appointmentId}/deny`,
      messagePatient: (appointmentId: string) =>
        `/api/provider-portal/appointments/${appointmentId}/message-patient`,
      offerTelehealth: (appointmentId: string) =>
        `/api/provider-portal/appointments/${appointmentId}/offer-telehealth`,
      requestCallback: (appointmentId: string) =>
        `/api/provider-portal/appointments/${appointmentId}/request-callback`,
    },
    leads: {
      list: "/api/provider-portal/leads",
      detail: (leadId: string) => `/api/provider-portal/leads/${leadId}`,
      confirm: (leadId: string) => `/api/provider-portal/leads/${leadId}/confirm`,
      cancel: (leadId: string) => `/api/provider-portal/leads/${leadId}/cancel`,
      documents: (leadId: string) => `/api/provider-portal/leads/${leadId}/documents`,
      documentAccess: (leadId: string, documentId: string) =>
        `/api/provider-portal/leads/${leadId}/documents/${documentId}/access`,
    },
    patients: {
      detail: (patientId: string) => `/api/provider-portal/patients/${patientId}`,
    },
    management: {
      context: "/api/provider-portal/management/context",
      providers: "/api/provider-portal/management/providers",
      providerSubscriptionSummary: (childProviderId: string) =>
        `/api/provider-portal/management/providers/${childProviderId}/subscription-summary`,
      removeChildSubscription: (childProviderId: string) =>
        `/api/provider-portal/management/providers/${childProviderId}/remove-child-subscription`,
      overview: "/api/provider-portal/management/overview",
      parentVisibility: "/api/provider-portal/management/parent-visibility",
      providerInheritance: "/api/provider-portal/management/providers/inheritance",
      searchProviders: "/api/provider-portal/management/providers/search",
      linkExistingProvider: "/api/provider-portal/management/providers/link-existing",
      createChildProvider: "/api/provider-portal/management/providers/create-child",
      unlinkChildProvider: (childProviderId: string) =>
        `/api/provider-portal/management/providers/${childProviderId}/unlink`,
    },
    settings: {
      schedule: "/api/provider-portal/settings/schedule",
      onboarding: "/api/provider-portal/settings/onboarding",
      approvalStatus: "/api/provider-portal/profile/approval-status",
      submitForReview: "/api/provider-portal/profile/submit-for-review",
      profile: "/api/provider-portal/settings/profile",
      profilePhoto: "/api/provider-portal/settings/profile/photo",
      addresses: "/api/provider-portal/settings/addresses",
      addressItem: (addressId: string) =>
        `/api/provider-portal/settings/addresses/${addressId}`,
      general: "/api/provider-portal/settings/general",
      leadScoring: "/api/provider-portal/settings/lead-scoring",
      googleReviews: "/api/provider-portal/settings/google-reviews",
      connectGoogleReviews: "/api/provider-portal/settings/google-reviews/connect",
      specialties: "/api/provider-portal/settings/specialties",
      specialtyItem: (specialtyId: string) =>
        `/api/provider-portal/settings/specialties/${specialtyId}`,
      insurances: "/api/provider-portal/settings/insurances",
      insuranceItem: (insuranceProviderId: string) =>
        `/api/provider-portal/settings/insurances/${insuranceProviderId}`,
      insuranceRecordItem: (providerInsurancePlanId: string) =>
        `/api/provider-portal/settings/insurances/records/${providerInsurancePlanId}`,
    },
    staff: {
      list: "/api/provider-portal/staff",
      availability: (staffId: string) =>
        `/api/provider-portal/staff/${staffId}/availability`,
      availabilityItem: (staffId: string, availabilityId: string) =>
        `/api/provider-portal/staff/${staffId}/availability/${availabilityId}`,
      unavailability: (staffId: string) =>
        `/api/provider-portal/staff/${staffId}/unavailability`,
      unavailabilityItem: (staffId: string, recordId: string) =>
        `/api/provider-portal/staff/${staffId}/unavailability/${recordId}`,
    },
    support: {
      tickets: "/api/provider-portal/support/tickets",
      ticket: (ticketId: string) => `/api/provider-portal/support/tickets/${ticketId}`,
      messages: (ticketId: string) => `/api/provider-portal/support/tickets/${ticketId}/messages`,
    },
    messages: {
      threads: "/api/provider-portal/messages/threads",
      thread: (threadId: string) => `/api/provider-portal/messages/threads/${threadId}`,
      unreadCount: "/api/provider-portal/messages/unread-count",
      threadMessages: (threadId: string) => `/api/provider-portal/messages/threads/${threadId}/messages`,
      threadDocuments: (threadId: string) => `/api/provider-portal/messages/threads/${threadId}/documents`,
      messageDocuments: (threadId: string, messageId: string) =>
        `/api/provider-portal/messages/threads/${threadId}/messages/${messageId}/documents`,
      documentAccess: (threadId: string, documentId: string) =>
        `/api/provider-portal/messages/threads/${threadId}/documents/${documentId}/access`,
      documentDelete: (threadId: string, documentId: string) =>
        `/api/provider-portal/messages/threads/${threadId}/documents/${documentId}`,
    },
    availability: {
      provider: "/api/provider-portal/availability/provider",
      providerItem: (availabilityId: string) =>
        `/api/provider-portal/availability/provider/${availabilityId}`,
    },
    unavailability: {
      provider: "/api/provider-portal/unavailability/provider",
      providerItem: (recordId: string) =>
        `/api/provider-portal/unavailability/provider/${recordId}`,
    },
  },
  provider: {
    billing: {
      summary: "/api/provider/billing/summary",
      charges: "/api/provider/billing/charges",
      transactions: "/api/provider/billing/transactions",
      outstandingPayNow: "/api/provider/billing/outstanding/pay-now",
      subscriptionCheckout: "/api/provider/billing/subscriptions/checkout",
    },
    subscription: {
      plans: "/api/provider/subscription/plans",
      current: "/api/provider/subscription/current",
      selectPlanPreview: "/api/provider/subscription/select-plan-preview",
    },
  },
  ai: {
    analyze: "/api/ai/analyze",
    publicSearch: "/api/ai/public/search",
    conversationMessage: "/api/ai/conversation/message",
  },
  whatsapp: {
    results: (sessionId: string, viewerToken?: string) =>
      viewerToken?.trim()
        ? `/api/whatsapp/results/${sessionId}?vt=${encodeURIComponent(viewerToken)}`
        : `/api/whatsapp/results/${sessionId}`,
  },
  verification: {
    session: "/api/verification/session",
  },
  serviceIntegrations: {
    list: "/api/service-integrations",
  },
  providers: {
    specialties: "/api/public/providers/specialties",
    requestDemo: "/api/public/providers/request-demo",
    signup: "/api/public/providers/signup",
    citySpotlights: "/api/providers/city-spotlights",
    discover: "/api/providers/discover",
    publicDiscover: "/api/providers/public/discover",
    detail: (providerId: string) => `/api/providers/${providerId}`,
    featured: "/api/providers/featured",
    saved: "/api/providers/saved",
    savedIds: "/api/providers/saved/ids",
    save: (providerId: string) => `/api/providers/${providerId}/save`,
    leadSettings: (providerId: string) => `/api/providers/${providerId}/lead-settings`,
  },
  masterdata: {
    languages: "/api/masterdata/languages",
    insuranceProviders: "/api/masterdata/insurance-providers",
    insuranceProviderPlans: (insuranceProviderId: string) =>
      `/api/masterdata/insurance-providers/${insuranceProviderId}/plans`,
    insurancePlans: "/api/masterdata/insurance-plans",
  },
  findCare: {
    resolve: "/api/find-care/resolve",
  },
  features: {
    rxPricing: {
      search: "/api/features/rx-pricing/search",
      click: "/api/features/rx-pricing/click",
    },
  },
  homeRemedies: {
    symptoms: "/api/home-remedies/symptoms",
    bySymptom: (symptomId: string) => `/api/home-remedies/by-symptom/${symptomId}`,
    bySymptomName: (symptomName: string) =>
      `/api/home-remedies/by-symptom-name/${encodeURIComponent(symptomName)}`,
  },
  patientSupport: {
    request: "/api/patient-support/request",
    providerGrowthRequest: "/api/patient-support/provider-growth-request",
    resources: "/api/patient-support/resources",
    careGuide: "/api/patient-support/care-guide",
    careGuideById: (id: string) => `/api/patient-support/care-guide/${id}`,
  },
  appointments: {
    guestEmailOtpSend: "/api/appointments/guest-email-otp/send",
    guestEmailOtpVerify: "/api/appointments/guest-email-otp/verify",
    mine: "/api/appointments/mine",
    bookingRequests: "/api/appointments/mine/booking-requests",
    bookingRequestDetail: (leadId: string) =>
      `/api/appointments/mine/booking-requests/${leadId}`,
    bookingRequestDocuments: (leadId: string) =>
      `/api/appointments/mine/booking-requests/${leadId}/documents`,
    bookingRequestDocumentAccess: (leadId: string, documentId: string) =>
      `/api/appointments/mine/booking-requests/${leadId}/documents/${documentId}/access`,
    cancel: (appointmentId: string) => `/api/appointments/${appointmentId}/cancel`,
    search: {
      book: (providerId: string) => `/api/appointments/search/providers/${providerId}`,
      request: (providerId: string) => `/api/appointments/search/providers/${providerId}/request`,
      availability: (providerId: string) =>
        `/api/appointments/search/providers/${providerId}/availability`,
    },
  },
  messages: {
    threads: "/api/messages/threads",
    thread: (threadId: string) => `/api/messages/threads/${threadId}`,
    unreadCount: "/api/messages/unread-count",
    threadMessages: (threadId: string) => `/api/messages/threads/${threadId}/messages`,
    documents: "/api/messages/documents",
    threadDocuments: (threadId: string) => `/api/messages/threads/${threadId}/documents`,
    messageDocuments: (threadId: string, messageId: string) =>
      `/api/messages/threads/${threadId}/messages/${messageId}/documents`,
    documentAccess: (threadId: string, documentId: string) =>
      `/api/messages/threads/${threadId}/documents/${documentId}/access`,
    documentDelete: (threadId: string, documentId: string) =>
      `/api/messages/threads/${threadId}/documents/${documentId}`,
  },
  user: {
    detail: (userId: string) => `/api/user/${userId}`,
    profile: "/api/user/profile",
    phoneVerification: {
      send: "/api/user/profile/phone-verification/send",
      check: "/api/user/profile/phone-verification/check",
    },
    profileCompletion: "/api/user/profile/completion",
    profilePhoto: "/api/user/profile/photo",
    insurance: {
      list: "/api/user/insurance",
      providers: "/api/user/insurance/providers",
      plans: "/api/user/insurance/plans",
      item: (insuranceId: string) => `/api/user/insurance/${insuranceId}`,
      validate: (insuranceId: string) =>
        `/api/user/insurance/${insuranceId}/validate`,
      document: (insuranceId: string) =>
        `/api/user/insurance/${insuranceId}/document`,
      frontCard: (insuranceId: string) =>
        `/api/user/insurance/${insuranceId}/front-card`,
      backCard: (insuranceId: string) =>
        `/api/user/insurance/${insuranceId}/back-card`,
      frontCardDownload: (insuranceId: string) =>
        `/api/user/insurance/${insuranceId}/front-card/download`,
      backCardDownload: (insuranceId: string) =>
        `/api/user/insurance/${insuranceId}/back-card/download`,
    },
    documentSharing: {
      categories: "/api/user/document-sharing/categories",
      providers: "/api/user/document-sharing/providers",
      policies: "/api/user/document-sharing/policies",
      policyProvider: (providerId: string) =>
        `/api/user/document-sharing/policies/${providerId}`,
    },
    conditions: {
      master: "/api/user/conditions/master",
      topSearchSymptoms: "/api/user/conditions/top-search-symptoms",
      list: "/api/user/conditions",
      item: (conditionId: string) => `/api/user/conditions/${conditionId}`,
    },
    medications: {
      list: "/api/user/medications",
      item: (medicationId: string) => `/api/user/medications/${medicationId}`,
    },
    tracker: {
      dashboard: "/api/user/tracker/dashboard",
      insights: "/api/user/tracker/insights",
      routines: "/api/user/tracker/routines",
      routine: (routineId: string) => `/api/user/tracker/routines/${routineId}`,
      routineEntries: (routineId: string) =>
        `/api/user/tracker/routines/${routineId}/entries`,
    },
    searchHistory: {
      list: "/api/user/search-history",
      recent: "/api/user/search-history/recent",
      detail: (id: string) => `/api/user/search-history/${id}`,
      searchAgain: (id: string) => `/api/user/search-history/${id}/search-again`,
    },
  },
  patient: {
    billing: {
      summary: "/api/patient/billing/summary",
      charges: "/api/patient/billing/charges",
      transactions: "/api/patient/billing/transactions",
    },
    familyMembers: {
      list: "/api/patient/family-members",
      create: "/api/patient/family-members",
      update: (id: string) => `/api/patient/family-members/${id}`,
      remove: (id: string) => `/api/patient/family-members/${id}`,
      bookingOptions: "/api/patient/family-members/options/booking",
    },
    subscription: {
      plans: "/api/patient/subscription/plans",
      current: "/api/patient/subscription/current",
      selectPlanPreview: "/api/patient/subscription/select-plan-preview",
      createCheckoutSession: "/api/patient/subscription/create-checkout-session",
    },
    insuranceProfiles: {
      options: "/api/patient/insurance-profiles/options",
    },
  },
} as const;

export function getScopedAuthApiBase(scope: AuthScope = "user") {
  if (scope === "admins") return API_ROUTES.admins.auth.base;
  if (scope === "providers") return API_ROUTES.providerPortal.auth.base;
  return API_ROUTES.auth.base;
}
